import React from 'react';
import { ShoppingCart } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';

interface GlobalCartProps {
    isDark: boolean;
}

export const GlobalCart: React.FC<GlobalCartProps> = ({ isDark }) => {
    const { profile, user } = useUser();
    const navigate = useNavigate();
    const location = useLocation();

    const cartItemsCount = profile?.cart?.reduce((acc, item) => acc + item.quantity, 0) || 0;

    // Don't render on admin, login, checkout, or cart itself
    const isHiddenRoute =
        location.pathname.startsWith('/admin') ||
        location.pathname === '/checkout' ||
        location.pathname === '/login';

    if (isHiddenRoute) return null;

    return (
        <button
            onClick={() => {
                // We use a query param or state if we want to trigger cart view,
                // Since cart is part of HomePage state in original code, we can navigate home and pass state
                // Or refactor Cart to its own route. For simplicity, we navigate to home and use local state logic later,
                // or just let a centralized handler open it. A better way: navigate('/cart') if we had a route for it.
                // For now, let's keep the user's setup but trigger CartView component logic by replacing local state.

                // Since CartView is handled in HomePage conditionally, the cleanest way without a major refactor is to 
                // rely on localStorage or a global context for 'currentView'. But actually:
                // Let's use localStorage like the original code did.
                localStorage.setItem('currentPage', 'cart');
                navigate('/', { state: { view: 'cart', clearModals: true } });
            }}
            className={`hidden md:flex fixed bottom-6 left-6 md:bottom-10 md:left-10 z-[9999] w-12 h-12 md:w-14 md:h-14 rounded-full items-center justify-center shadow-lg transition-all duration-300 backdrop-blur-md border hover:scale-110 active:scale-95 group animate-fade-in ${isDark ? 'bg-slate-900/60 border-white/10 text-white shadow-[0_0_20px_rgba(0,0,0,0.3)] hover:shadow-[0_0_25px_rgba(59,130,246,0.3)] hover:bg-slate-800/80' : 'bg-white/80 border-white/60 text-slate-700 shadow-[0_10px_25px_rgba(0,0,0,0.1)] hover:shadow-[0_15px_30px_rgba(0,0,0,0.15)] hover:bg-white'}`}
        >
            <div className="relative">
                <ShoppingCart className={`w-5 h-5 md:w-6 md:h-6 transition-colors ${isDark ? 'text-blue-100' : 'text-slate-600 group-hover:text-blue-600'}`} strokeWidth={2} />
                <div className={`absolute -top-1.5 -right-1.5 w-4 h-4 md:w-4.5 md:h-4.5 bg-rose-500 rounded-full flex items-center justify-center text-[9px] md:text-[10px] font-bold text-white border-[2px] ${isDark ? 'border-slate-900' : 'border-white'} shadow-sm`}>
                    {cartItemsCount}
                </div>
            </div>
        </button>
    );
};
